import type { ApiError, ConfirmView, SignRequest, SignResponse } from '@sign/shared';

/**
 * In production the Worker serves both this SPA and the API from the same
 * origin, so the base is '' (relative `/api/...`). For local dev, set
 * VITE_API_BASE=http://localhost:8787 (see h5/.env.development).
 */
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '';

/** An Error that carries the HTTP status + parsed `ApiError` body, if any. */
export class HttpError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response): Promise<HttpError> {
  let code = 'http_error';
  let message = `请求失败（${res.status}）`;
  try {
    const body = (await res.json()) as Partial<ApiError>;
    if (body && typeof body === 'object') {
      if (typeof body.error === 'string') code = body.error;
      if (typeof body.message === 'string') message = body.message;
    }
  } catch {
    // Non-JSON error body — keep the generic message.
  }
  return new HttpError(res.status, code, message);
}

/** GET /api/confirm/:token — fetch the signer-facing view. */
export async function fetchConfirm(token: string): Promise<ConfirmView> {
  const res = await fetch(`${API_BASE}/api/confirm/${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as ConfirmView;
}

/** POST /api/confirm/:token/sign — submit the signature PNG. */
export async function submitSignature(
  token: string,
  body: SignRequest,
): Promise<SignResponse> {
  const res = await fetch(`${API_BASE}/api/confirm/${encodeURIComponent(token)}/sign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as SignResponse;
}

/** POST /api/confirm/:token/sms — request an SMS verification code. */
export async function sendSmsCode(token: string, phone: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/confirm/${encodeURIComponent(token)}/sms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) throw await parseError(res);
}

/**
 * POST /api/confirm/:token/finalize — upload the composed archival sheet after
 * a multi-sign record collects its last signature.
 */
export async function finalizeSheet(token: string, imageBase64: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/confirm/${encodeURIComponent(token)}/finalize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ imageBase64 }),
  });
  if (!res.ok) throw await parseError(res);
}
